// src/pages/ManualMarketing/ManualMarketingGallery/components/AIStyleOptionSheet.jsx

import React, { useState, useRef, useEffect, useMemo } from "react";
import useImageListStore from "../../../../stores/imageList";
import { BottomWithButton } from "../../../../assets/components/BottomSheetGroup";
import { SplitRowQuarter } from "../../../../assets/components/ButtonGroup";
import { Chip } from "../../../../assets/components/Chip";
import { useFixedLayout } from "../../../../hooks/useFixedLayout";

const styleOptions = [
  { key: "1", label: "3D 이미지" },
  { key: "2", label: "실사 이미지" },
  { key: "3", label: "캐릭터/만화" },
  { key: "4", label: "레트로" },
  { key: "5", label: "AI모델" },
  { key: "6", label: "예술" },
];

const STYLE_KEYS = styleOptions.map((o) => o.key);

// ✅ URL 비교용 헬퍼 (도메인/포트/프로토콜 무시)
const normalizePath = (p) => {
  if (!p) return "";
  try {
    // 전체 URL이면 pathname만
    if (p.startsWith("http")) {
      const u = new URL(p);
      return u.pathname; // 예: /uploads/thumbnail/77/6/thumbnail_3_thumb.jpg
    }
  } catch (e) {
    // new URL 실패하면 그대로 진행
  }
  // /uploads 뒤만 잘라내기
  const idx = p.indexOf("/uploads");
  return idx >= 0 ? p.slice(idx) : p;
};

const AIStyleOptionSheet = ({
  exData,
  aiStyle,
  value,
  updateData,
  isOpen,
  onClose,
  onConfirm,
  headerSelectedPath,
}) => {
  const { getImageList } = useImageListStore();
  const { headerRef, bottomSpace } = useFixedLayout(isOpen);

  // 매장 번호 기준으로 이미지 리스트 가져오기
  const storeBusinessNumber =
    exData?.store_business_number ||
    (typeof localStorage !== "undefined"
      ? localStorage.store_business_number
      : null);

  // ✅ imageManualList 를 useMemo 로 고정해서 useEffect deps 경고 제거
  const imageManualList = useMemo(() => {
    if (!storeBusinessNumber) return [];
    const list = getImageList(storeBusinessNumber);
    return list || [];
  }, [storeBusinessNumber, getImageList]);

  // 로컬스토리지에서 aiStyle도 시도
  const aiStyleFromLS =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("aiStyle")
      : null;

  // 1차: 추천 스타일 키 (aiStyle prop > localStorage.aiStyle)
  const recommendedKeyRef = useRef(String(aiStyle ?? aiStyleFromLS ?? ""));
  const RECO = recommendedKeyRef.current;

  // 2차: 추천값 없을 때 랜덤 스타일 키
  const randomStyleKeyRef = useRef(
    RECO || STYLE_KEYS[Math.floor(Math.random() * STYLE_KEYS.length)]
  );

  const [selectedStyle, setSelectedStyle] = useState(
    RECO || randomStyleKeyRef.current || ""
  );
  const [selectedImage, setSelectedImage] = useState(null);
  const [didUserPickImage, setDidUserPickImage] = useState(false);
  const [, setDidUserChangeStyle] = useState(false);
  const pickedPathByStyleRef = useRef({});   // { [styleKey]: imagePath }
  const lastInitKeyRef = useRef("");

  const aiThumbnail = exData?.aiThumbnail;

  // ── 시트 열릴 때: "보기용" 선택 상태만 세팅 ──
  useEffect(() => {
    if (!isOpen) return;

    // ✅ 외부 입력이 동일하면 재초기화 금지 (열고/닫고로 랜덤 바뀌는 문제 해결)
    const initKey = [
      String(value || ""),
      String(aiStyle || ""),
      normalizePath(aiThumbnail || ""),
      normalizePath(headerSelectedPath || ""),
      String(imageManualList.length),
    ].join("|");
    if (lastInitKeyRef.current === initKey) return;
    lastInitKeyRef.current = initKey;

    let baseKey = String(
      value || aiStyle || RECO || randomStyleKeyRef.current || ""
    );
    let initialImg = null;

    const normalizedHeader = normalizePath(headerSelectedPath);
    const normalizedAiThumb = normalizePath(aiThumbnail);

    // ✅ 1순위: 마지막 저장 aiThumbnail
    if (normalizedAiThumb) {
      initialImg = imageManualList.find(
        (img) => normalizePath(img.path) === normalizedAiThumb
      );
      if (initialImg) {
        baseKey = String(initialImg.design_id ?? baseKey);
      }
    }

    // ✅ 2순위: Header 에서 선택한 이미지
    if (!initialImg && normalizedHeader) {
      const headerImg = imageManualList.find(
        (img) => normalizePath(img.path) === normalizedHeader
      );
      if (headerImg) {
        initialImg = headerImg;
        baseKey = String(headerImg.design_id ?? baseKey);
      }
    }

    // ✅ 2.5) 이전에 뽑아둔(혹은 사용자가 골랐던) 이미지가 있으면 재사용
    if (!initialImg && baseKey) {
      const savedPath = pickedPathByStyleRef.current[String(baseKey)];
      if (savedPath) {
        const saved = imageManualList.find((img) => img.path === savedPath);
        if (saved) initialImg = saved;
      }
    }

    // ✅ 3순위: baseKey 스타일 내 랜덤
    if (!initialImg && baseKey) {
      const candidates = imageManualList.filter(
        (img) => String(img.design_id) === baseKey
      );
      if (candidates.length > 0) {
        initialImg =
          candidates[Math.floor(Math.random() * candidates.length)];
        pickedPathByStyleRef.current[String(baseKey)] = initialImg.path; // ✅ 고정
      }
    }

    setSelectedStyle(baseKey);
    setSelectedImage(initialImg || null);
    setDidUserPickImage(false);
    setDidUserChangeStyle(false);
  }, [
    isOpen,
    value,
    aiStyle,
    RECO,
    imageManualList,
    aiThumbnail,
    headerSelectedPath,
  ]);

  useEffect(() => {
    if (!isOpen) {
      lastInitKeyRef.current = "";   // ✅ 다음 open 때 초기화(useEffect) 다시 타게
    }
  }, [isOpen]);

  // 현재 선택된 스타일 기준 이미지 리스트
  const filteredImages = useMemo(
    () =>
      imageManualList.filter(
        (img) => String(img.design_id) === String(selectedStyle || "")
      ),
    [imageManualList, selectedStyle]
  );

  // ── 드래그 스크롤 + AI 추천 Chip 자동 스크롤 ──
  const containerRef = useRef(null);
  const chipRefs = useRef({}); // key별 chip DOM 저장
  const drag = useRef({ isDragging: false, startX: 0, scrollLeft: 0 });
  const imageContainerRef = useRef(null); // 이미지 전체 스크롤 영역
  const imageRefs = useRef({}); // 각 이미지 DOM 저장용

  const handleMouseDown = (e) => {
    if (!containerRef.current) return;
    drag.current.isDragging = true;
    drag.current.startX = e.pageX - containerRef.current.offsetLeft;
    drag.current.scrollLeft = containerRef.current.scrollLeft;
  };

  const handleMouseLeave = () => {
    drag.current.isDragging = false;
  };

  const handleMouseUp = () => {
    drag.current.isDragging = false;
  };

  const handleMouseMove = (e) => {
    if (!drag.current.isDragging || !containerRef.current) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = x - drag.current.startX;
    containerRef.current.scrollLeft = drag.current.scrollLeft - walk;
  };

  // ✅ 시트 열릴 때 AI 추천 Chip 이 보이도록 스크롤
  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => scrollChipIntoView(String(selectedStyle || RECO || ""), "smooth"));
  }, [isOpen, selectedStyle, RECO]);




  // 상하 스크롤
  useEffect(() => {
    if (!isOpen || !selectedImage || !imageContainerRef.current) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollImageFullyIntoView(selectedImage.path, "smooth", 12);
      });
    });
  }, [isOpen, selectedImage]);


  // 스타일 선택 시 스크롤 헬퍼
  const scrollChipIntoView = (key, behavior = "smooth") => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const el = chipRefs.current[key];
    if (!el) return;

    const chipLeft = el.offsetLeft;
    const chipWidth = el.offsetWidth;
    const containerWidth = container.clientWidth;

    const nextLeft = Math.max(
      0,
      Math.min(
        chipLeft - (containerWidth - chipWidth) / 2,
        container.scrollWidth - containerWidth
      )
    );

    container.scrollTo({ left: nextLeft, behavior });
  };
  // 이미지 선택 헬퍼
  const scrollImageFullyIntoView = (path, behavior = "smooth", margin = 12) => {
    const container = imageContainerRef.current;
    const el = imageRefs.current[path];
    if (!container || !el) return;

    const c = container.getBoundingClientRect();
    const r = el.getBoundingClientRect();

    // el이 컨테이너 위로 잘렸으면 위쪽으로, 아래로 잘렸으면 아래쪽으로 이동
    const topOverflow = r.top - c.top - margin;        // 음수면 위로 잘림
    const bottomOverflow = r.bottom - c.bottom + margin; // 양수면 아래로 잘림

    let nextTop = container.scrollTop;

    if (topOverflow < 0) nextTop += topOverflow;          // 위로 올림
    else if (bottomOverflow > 0) nextTop += bottomOverflow; // 아래로 내림
    else return; // 이미 완전히 보이는 상태면 종료

    const maxTop = container.scrollHeight - container.clientHeight;
    nextTop = Math.max(0, Math.min(nextTop, maxTop));

    container.scrollTo({ top: nextTop, behavior });
  };




  // ── 스타일 칩 선택 ───────────────────────────────
  const handleSelectStyle = (styleKey) => {
    const s = String(styleKey);

    if (s === String(selectedStyle)) return;

    const candidates = imageManualList.filter(
      (img) => String(img.design_id) === s
    );
    const savedPath = pickedPathByStyleRef.current[s];
    const savedImage =
      savedPath && candidates.length > 0
        ? candidates.find((img) => img.path === savedPath) || null
        : null;
    const nextImage = savedImage || candidates[0] || null;

    setSelectedStyle(s);
    // 스타일이 바뀌면 이전 스타일 이미지 선택 즉시 해제
    setSelectedImage(nextImage);
    setDidUserPickImage(!!nextImage);
    setDidUserChangeStyle(true);

    requestAnimationFrame(() => scrollChipIntoView(s));
  };

  // ── 이미지 선택 ─────────────────────────────────
  const handleSelectImage = (img) => {
    if (!img) return;
    if (selectedImage?.path === img.path) return;

    setSelectedImage(img);
    setDidUserPickImage(true);
    pickedPathByStyleRef.current[String(selectedStyle)] = img.path;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollImageFullyIntoView(img.path, "smooth", 12);
      });
    });

  };

  const handleSave = () => {
    // 2순위: 외부(부모/AI 자동 선택)에서 넘어온 prompt
    const externalPrompt = (exData?.prompt || "").trim();
    const hasExternalPrompt = externalPrompt !== "";

    let promptToUse = "";
    let finalImage = selectedImage;

    // 1) 사용자가 이미지 직접 클릭한 경우
    if (didUserPickImage && finalImage?.prompt?.trim()) {
      promptToUse = finalImage.prompt.trim();

      // 2) 이미지 클릭 안 했어도, 현재 선택된 이미지가 있으면 그걸 그대로 사용 (✅ 네가 원하는 동작)
    } else if (finalImage?.prompt?.trim()) {
      promptToUse = finalImage.prompt.trim();

      // 3) 선택된 이미지가 없을 때만 외부 prompt
    } else if (hasExternalPrompt) {
      promptToUse = externalPrompt;

      // 4) 그 외에만 랜덤 fallback
    } else {
      const candidates = filteredImages.filter((img) => img.prompt?.trim());
      if (candidates.length > 0) {
        finalImage = candidates[Math.floor(Math.random() * candidates.length)];
        promptToUse = finalImage.prompt.trim();
      } else {
        finalImage = filteredImages.length > 0
          ? filteredImages[Math.floor(Math.random() * filteredImages.length)]
          : null;
        promptToUse = "";
      }
    }
    const finalStyle = String(finalImage?.design_id ?? selectedStyle ?? RECO ?? "");

    const payload = {
      style: finalStyle,
      prompt: promptToUse,
      selectedImage: finalImage || null,
      aiThumbnail: finalImage?.path || null,
    };
    setSelectedStyle(finalStyle);
    updateData?.(payload);
    onConfirm?.(payload);
    onClose?.();
  };

  const handleClose = () => {
    // ❗ 닫기 = 아무것도 저장하지 않음
    onClose && onClose();
  };

  return (
    <BottomWithButton
      isOpen={isOpen}
      onClose={handleClose}
      title="이미지 스타일"
      subtitle="어떤 스타일로 변경하고 싶나요? (이미지 선택 1개)"
      height="h-[702px]"
      renderButton={({ onClick }) => (
        <SplitRowQuarter
          leftText="닫기"
          onLeft={handleClose}
          rightType="fill"
          rightText="저장"
          onRight={handleSave}
        />
      )}
    >
      <div className="flex flex-col h-full">
        <div ref={headerRef} className="shrink-0 bg-surface-default">
          {/* 스타일 Chip 리스트 */}
          <div
            ref={containerRef}
            className="flex overflow-x-auto space-x-[8px] no-scrollbar cursor-grab active:cursor-grabbing pb-[24px]"
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
          >
            {styleOptions.map((option) => {
              const isSelected = String(selectedStyle) === String(option.key);
              const isRecommended = RECO === String(option.key);

              return (
                <div
                  key={option.key}
                  ref={(el) => {
                    if (el) chipRefs.current[option.key] = el;
                  }}
                  className="flex-shrink-0"
                >
                  <Chip
                    onClick={() => handleSelectStyle(option.key)}
                    chip="a"
                    status={isSelected ? "selected" : "default"}
                    label={option.label}
                    allowBadge
                    badgeLabel={isRecommended ? "AI 추천" : undefined}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto no-scrollbar"
          style={{ paddingBottom: bottomSpace }}
          ref={imageContainerRef}>
          {/* 이미지 리스트 */}
          <div className="grid grid-cols-2 gap-[8px]"
          >
            {filteredImages.map((img, idx) => (
              <div
                key={idx}
                ref={(el) => {
                  if (el) imageRefs.current[img.path] = el;
                }}
                onClick={() => handleSelectImage(img)}
                className={`relative rounded-[12px] w-full overflow-hidden  transition `}
              >
                <img
                  src={img.path}
                  alt={`스타일 이미지 ${idx + 1}`}
                  className={`w-full h-full rounded-[12px] object-cover border-[2px] cursor-pointer ${selectedImage?.path === img.path ? "border-border-primary" : "border-transparent"}`}
                />

                {selectedImage && selectedImage.path !== img.path && (
                  <div className="absolute inset-0 bg-surface-white-dimmed_80 pointer-events-none" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </BottomWithButton>
  );
};

export default AIStyleOptionSheet;
